import { InfoCircleOutlined } from "@ant-design/icons";
import { Form, Input, Popover, Spin, Button, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import classNames from "classnames";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import FeatureActivityLinking from "components/bid-package-planning-panel/feature-activity-linking";
import CIQDatePicker from "components/custom-date-picker";
import FeatureLinkingDialog from "components/feature-linking-dialog";
import FeatureItem from "components/feature-linking-dialog/feature-item";
import FeatureLinksDialogHeader from "components/feature-linking-dialog/feature-links-dialog-header";
import FeaturesGrid from "components/feature-linking-dialog/features-grid";
import LinkIcon from "components/svg-icons/link-icon";
import { formatEntityTitle } from "config/page-titles";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes, LinkTypes } from "entity-app/constants";
import {
  useBidPackagePlanning,
  WorkflowInstanceMilestone,
  WorkflowInstanceOffset
} from "entity-app/hooks/bid-package-planning";
import useFeatureToFeatureLinks from "entity-app/hooks/feature-to-feature-links";
import { TEntityDateblock } from "../../models";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import dayjs from "utils/dayjs-config";
import { restrictInputToNumbers } from "utils/inpututils";
import { sortLinkedItems } from "utils/utils";
import {
  linkBidPackages,
  TOnsiteEstimateUpdateSummary
} from "services/bid-package-links";
import { getGQLQueryData } from "entity-app/services";
import { TBidPackageLog } from "pages/preconstruction-log-page/model";
import {
  isBidPackageCompleted,
  isOnsiteEstimateEditable
} from "pages/preconstruction-log-page/onsite-estimate";
import { QUERY_BID_PACKAGE_ONSITE_ESTIMATE_EDITABLE } from "pages/preconstruction-log-page/queries";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import {
  BID_PACKAGE_SUCCESSORS_COLUMN_WIDTH,
  LINKS_COLUMN_OUTER_PADDING_PX
} from "../../layout-constants";
import {
  getBidPackageLinkedMaterials,
  type TBidPackageLinkedMaterial
} from "../../utils/get-bid-package-linked-materials";
import {
  getBidPackageLinkedSubmittals,
  type TBidPackageLinkedSubmittal
} from "../../utils/get-bid-package-linked-submittals";
import "./bid-package-successors-column.scss";
import "../design-package-successors-column/design-package-successors-column.scss";

// Only include materials that are NOT linked to a submittal (exclude any material that has a submittal as a linked parent)
const MATERIAL_FEATURES_LIST_WHERE = {
  feature_type_id: { _eq: FeatureTypes.MATERIALS },
  deleted: { _eq: false },
  deleted_from_source: { _eq: false },
  implicit: { _eq: false }
};
const SUBMITTAL_FEATURES_LIST_WHERE = {
  feature_type_id: { _eq: FeatureTypes.SUBMITTALS },
  deleted: { _eq: false },
  deleted_from_source: { _eq: false },
  implicit: { _eq: false },
  feature_workflow_instance: { workflow_status: { _neq: "VOID" } }
};

const SUBMITTAL_PLAN_MILESTONE_NAMES = [
  "Assigned",
  "Submitted",
  "Approved"
] as const;

const MATERIAL_PLAN_MILESTONE_NAMES = ["Released", "Delivered"] as const;

type TPlanMilestoneDisplay = {
  id: string;
  name: string;
  planned_date?: string | null;
};

function resolvePlanMilestones(
  milestones: WorkflowInstanceMilestone[],
  defaultNames: readonly string[]
): TPlanMilestoneDisplay[] {
  const milestoneByName = new Map(
    milestones.map((milestone) => [milestone.name, milestone])
  );

  return defaultNames.map((name) => {
    const existing = milestoneByName.get(name);
    if (existing) {
      return {
        id: existing.id,
        name: existing.name,
        planned_date: existing.planned_date ?? null
      };
    }

    return {
      id: `default-${name.toLowerCase().replace(/\s+/g, "-")}`,
      name,
      planned_date: null
    };
  });
}

type TLinkMode = "activity" | "material" | "submittal";

const DATE_PLANNER_DESCRIPTION_BY_MODE: Record<TLinkMode, string> = {
  activity:
    "Link a Schedule Activity or set an estimate ROJ date, then review Material lead time and Submittal durations.",
  material:
    "Link a long-lead Material. Dates will be calculated from a Submittal Plan or its linked Submittals.",
  submittal: "Link a Submittal to create a plan for the Bid Package."
};

type TLinkedMaterial = TBidPackageLinkedMaterial;
type TLinkedSubmittal = TBidPackageLinkedSubmittal;

function buildMaterialLinkChanges(
  selectedFeatures: TLinkedMaterial[],
  linkedFeatures: TLinkedMaterial[] | undefined
) {
  const insertedLinks: TLinkedMaterial[] = [];
  const deletedLinks: TLinkedMaterial[] = [];
  const linkedFeaturesMap = new Map(
    linkedFeatures?.map((feature) => [feature.id, feature])
  );

  selectedFeatures.forEach((feature) => {
    const existingFeature = linkedFeaturesMap.get(feature.id);
    if (existingFeature) {
      linkedFeaturesMap.delete(feature.id);
    } else {
      insertedLinks.push(feature);
    }
  });

  linkedFeaturesMap.forEach((feature) => {
    deletedLinks.push(feature);
  });

  return { insertedLinks, deletedLinks };
}

function buildSubmittalLinkChanges(
  selectedFeatures: TLinkedSubmittal[],
  linkedFeatures: TLinkedSubmittal[] | undefined
) {
  const insertedLinks: TLinkedSubmittal[] = [];
  const deletedLinks: TLinkedSubmittal[] = [];
  const linkedFeaturesMap = new Map(
    linkedFeatures?.map((feature) => [feature.id, feature])
  );

  selectedFeatures.forEach((feature) => {
    const existingFeature = linkedFeaturesMap.get(feature.id);
    if (existingFeature) {
      linkedFeaturesMap.delete(feature.id);
    } else {
      insertedLinks.push(feature);
    }
  });

  linkedFeaturesMap.forEach((feature) => {
    deletedLinks.push(feature);
  });

  return { insertedLinks, deletedLinks };
}

function BidDatePlannerTabs(
  props: Readonly<{
    value: TLinkMode;
    onChange: (mode: TLinkMode) => void;
    activityCount: number;
    materialCount: number;
    submittalCount: number;
  }>
) {
  const { value, onChange, activityCount, materialCount, submittalCount } =
    props;

  const tabs: Array<{
    mode: TLinkMode;
    label: string;
    count: number;
    testId: string;
  }> = [
    {
      mode: "activity",
      label: "Activity",
      count: activityCount,
      testId: "bid-date-planner-mode-activity"
    },
    {
      mode: "material",
      label: "Materials",
      count: materialCount,
      testId: "bid-date-planner-mode-material"
    },
    {
      mode: "submittal",
      label: "Submittals",
      count: submittalCount,
      testId: "bid-date-planner-mode-submittal"
    }
  ];

  return (
    <div
      className="bid-date-planner-tabs"
      role="tablist"
      data-testid="bid-date-planner-mode-tabs"
    >
      {tabs.map((tab) => {
        const isActive = value === tab.mode;
        return (
          <button
            key={tab.mode}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={classNames("bid-date-planner-tab", {
              "bid-date-planner-tab--active": isActive
            })}
            onClick={() => onChange(tab.mode)}
            data-testid={tab.testId}
          >
            <span>{tab.label}</span>
            <span
              className="bid-date-planner-tab__count"
              data-testid={`${tab.testId}-count`}
            >
              <span className="bid-date-planner-tab__link-icon">
                <LinkIcon />
              </span>
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function BidDatePlannerInfoIcon() {
  return <InfoCircleOutlined className="bid-date-planner-info-icon" />;
}

function MilestoneDatesList(
  props: Readonly<{
    milestones: TPlanMilestoneDisplay[];
    loading?: boolean;
  }>
) {
  const { milestones, loading } = props;
  return (
    <Spin spinning={!!loading} size="small">
      <div className="space-y-1">
        {milestones.map((milestone) => (
          <div
            key={milestone.id}
            className="bid-date-planner-milestone-row"
            data-testid={`bid-plan-milestone-${milestone.name}`}
          >
            <span className="bid-date-planner-milestone-label">
              {milestone.name}
            </span>
            <span
              className={classNames("bid-date-planner-milestone-value", {
                "bid-date-planner-milestone-value--empty":
                  !milestone.planned_date
              })}
            >
              {milestone.planned_date
                ? DateUtils.format(milestone.planned_date, "MM-DD-YYYY")
                : "-"}
            </span>
          </div>
        ))}
      </div>
    </Spin>
  );
}

function PlanSection(
  props: Readonly<{
    title: string;
    offsets: WorkflowInstanceOffset[];
    milestones: TPlanMilestoneDisplay[];
    disabled?: boolean;
    loading?: boolean;
    showDottedDivider?: boolean;
    onOffsetBlur: (offsetId: string, value: string | null) => void;
  }>
) {
  const {
    title,
    offsets,
    milestones,
    disabled,
    loading,
    showDottedDivider,
    onOffsetBlur
  } = props;
  const sectionId = title.toLowerCase().replace(/\s+/g, "-");

  return (
    <div
      className="bid-date-planner-section"
      data-testid={`bid-plan-section-${sectionId}`}
    >
      <div className="bid-date-planner-section-title">{title}</div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {offsets.map((offset) => (
            <div key={offset.id}>
              <label className="bid-date-planner-field-label">
                {offset.name}
              </label>
              <Input
                className="bid-date-planner-offset-input"
                disabled={disabled}
                defaultValue={offset.duration ?? undefined}
                placeholder="0"
                type="number"
                suffix="Days"
                onKeyDown={restrictInputToNumbers}
                onBlur={(e) => onOffsetBlur(offset.id, e.target.value || null)}
                min={0}
                data-testid="bid-plan-offset-input"
              />
            </div>
          ))}
        </div>
        <MilestoneDatesList milestones={milestones} loading={loading} />
      </div>
      {showDottedDivider ? (
        <hr className="bid-date-planner-divider bid-date-planner-divider--dotted" />
      ) : null}
    </div>
  );
}

function getFeatureIdsWithLinks(
  features: Array<Record<string, any>> | null | undefined,
  linksField: "linked_submittals" | "linked_materials"
): Set<string> {
  return new Set(
    (features ?? [])
      .filter((feature) => feature[linksField]?.length)
      .map((feature) => feature.id)
  );
}

type TRenderActivityCardTooltip = (
  card: React.ReactNode,
  link: Record<string, unknown>,
  instance: Record<string, unknown>
) => React.ReactNode;

function mapPlanningActivityLinkToInstance(
  link: Record<string, any>
): Record<string, unknown> {
  const raw = (link.target_activities_instance ?? {}) as Record<string, any>;
  const activity = raw.feature_activities?.[0];
  return {
    ...raw,
    feature_type_id: FeatureTypes.ACTIVITIES,
    number: raw.number ?? activity?.source_task_id ?? raw.auto_inc_value
  };
}

function LinkedEntityItem(props: Readonly<{ title?: string; testId: string }>) {
  return (
    <div className="bid-date-planner-linked-item" data-testid={props.testId}>
      <div className="shrink-0 flex items-center justify-center">
        <LinkIcon />
      </div>
      <div className="bid-date-planner-linked-item__title" title={props.title}>
        {props.title}
      </div>
    </div>
  );
}

function BidSuccessorsLinkAction(
  props: Readonly<{
    children: React.ReactNode;
    onClick: () => void;
    testId: string;
  }>
) {
  return (
    <button
      type="button"
      className="design-successors-link-action"
      onClick={props.onClick}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

/** Manage-links dialog title with the Bid Package name, matching the
 * Activity linking dialog header. */
function ManageLinksDialogTitle(
  props: Readonly<{ title: string; bidPackageName?: string }>
) {
  const { title, bidPackageName } = props;
  return (
    <div className="flex items-center min-w-0 pr-8">
      <span className="shrink-0">{title}</span>
      {bidPackageName ? (
        <div className="flex items-center min-w-0 ml-4 pl-4 border-0 border-l border-solid border-gray-300 text-xs font-normal">
          <span className="mr-2 shrink-0">Bid Package:</span>
          <TruncatedTextWithTooltip
            text={bidPackageName}
            className="font-medium"
            dataTestId="manage-links-dialog-bid-package-name"
          />
        </div>
      ) : null}
    </div>
  );
}

function BidDatePlannerGroup(
  props: Readonly<{
    groupId: number;
    label: string;
    headerAction?: React.ReactNode;
    children: React.ReactNode;
  }>
) {
  const { groupId, label, headerAction, children } = props;

  return (
    <div
      className="bid-date-planner-group"
      data-testid={`links-column-group-${groupId}`}
    >
      <div
        className="bid-date-planner-group__header"
        data-testid="links-column-group-header"
      >
        <span
          className="bid-date-planner-group__label"
          data-testid="links-column-group-label"
        >
          {label}
        </span>
        {headerAction}
      </div>
      <div
        className="bid-date-planner-group__content"
        data-testid="links-column-group-content"
      >
        {children}
      </div>
    </div>
  );
}

function BidDatePlannerEmptyLinks(
  props: Readonly<{
    label: string;
    disabled?: boolean;
    onLinkClick: () => void;
    testId: string;
    linkTestId: string;
  }>
) {
  return (
    <div
      className="bid-date-planner-empty-row bid-date-planner-empty-row--standalone"
      data-testid={props.testId}
    >
      <span className="bid-date-planner-empty-row__label">{props.label}</span>
      {!props.disabled ? (
        <BidSuccessorsLinkAction
          onClick={props.onLinkClick}
          testId={props.linkTestId}
        >
          Add Link
        </BidSuccessorsLinkAction>
      ) : null}
    </div>
  );
}

function mapLinkedMaterialsForDialog(
  linkedFeatures: any[] | null,
  fallbackMaterials: TLinkedMaterial[]
): TLinkedMaterial[] {
  if (!linkedFeatures?.length) return fallbackMaterials;

  return linkedFeatures.map((feature) => ({
    id: feature.id,
    number: feature.number,
    title: feature.title,
    auto_inc_value: feature.auto_inc_value
  }));
}

function mapLinkedSubmittalsForDialog(
  linkedFeatures: any[] | null,
  fallbackSubmittals: TLinkedSubmittal[]
): TLinkedSubmittal[] {
  if (!linkedFeatures?.length) return fallbackSubmittals;

  return linkedFeatures.map((feature) => ({
    id: feature.id,
    number: feature.number,
    title: feature.title,
    auto_inc_value: feature.auto_inc_value
  }));
}

function MaterialLinkGroup({
  linkedMaterials,
  materialLinkCards,
  materialHeaderEditLinks,
  disabled,
  onLinkClick
}: Readonly<{
  linkedMaterials: TLinkedMaterial[];
  materialLinkCards?: React.ReactNode;
  materialHeaderEditLinks: React.ReactNode;
  disabled?: boolean;
  onLinkClick: () => void;
}>) {
  if (linkedMaterials.length === 0) {
    return (
      <BidDatePlannerEmptyLinks
        label="No Material Linked"
        disabled={disabled}
        onLinkClick={onLinkClick}
        testId="bid-materials-empty"
        linkTestId="bid-materials-link-button"
      />
    );
  }

  return (
    <BidDatePlannerGroup
      groupId={-12}
      label="Linked Materials"
      headerAction={materialHeaderEditLinks}
    >
      <div className="bid-date-planner-stack" data-testid="bid-materials-list">
        {materialLinkCards ??
          linkedMaterials.map((material) => (
            <LinkedEntityItem
              key={material.id}
              title={material.title}
              testId={`bid-linked-material-${material.id}`}
            />
          ))}
      </div>
    </BidDatePlannerGroup>
  );
}

function SubmittalLinkGroup({
  linkedSubmittals,
  submittalLinkCards,
  submittalHeaderEditLinks,
  disabled,
  onLinkClick
}: Readonly<{
  linkedSubmittals: TLinkedSubmittal[];
  submittalLinkCards?: React.ReactNode;
  submittalHeaderEditLinks: React.ReactNode;
  disabled?: boolean;
  onLinkClick: () => void;
}>) {
  if (linkedSubmittals.length === 0) {
    return (
      <BidDatePlannerEmptyLinks
        label="No Submittal Linked"
        disabled={disabled}
        onLinkClick={onLinkClick}
        testId="bid-submittals-empty"
        linkTestId="bid-submittals-link-button"
      />
    );
  }

  return (
    <BidDatePlannerGroup
      groupId={-13}
      label="Linked Submittals"
      headerAction={submittalHeaderEditLinks}
    >
      <div className="bid-date-planner-stack" data-testid="bid-submittals-list">
        {submittalLinkCards ??
          linkedSubmittals.map((submittal) => (
            <LinkedEntityItem
              key={submittal.id}
              title={submittal.title}
              testId={`bid-linked-submittal-${submittal.id}`}
            />
          ))}
      </div>
    </BidDatePlannerGroup>
  );
}

function BidPlanSections({
  submittalPlan,
  materialPlan,
  disabled,
  isCalculatingSubmittalPlan,
  isCalculatingMaterialPlan,
  onOffsetBlur
}: Readonly<{
  submittalPlan: any;
  materialPlan: any;
  disabled?: boolean;
  isCalculatingSubmittalPlan: boolean;
  isCalculatingMaterialPlan: boolean;
  onOffsetBlur: (
    offsetId: string,
    value: string | null,
    feature: "submittal" | "material"
  ) => void;
}>) {
  return (
    <div data-testid="bid-date-planner-cards">
      {submittalPlan ? (
        <PlanSection
          title="Submittal Plan"
          offsets={submittalPlan.workflow_instance_offsets ?? []}
          milestones={resolvePlanMilestones(
            submittalPlan.workflow_instance_milestones ?? [],
            SUBMITTAL_PLAN_MILESTONE_NAMES
          )}
          disabled={disabled}
          loading={isCalculatingSubmittalPlan}
          showDottedDivider={Boolean(materialPlan)}
          onOffsetBlur={(offsetId, value) =>
            onOffsetBlur(offsetId, value, "submittal")
          }
        />
      ) : null}
      {materialPlan ? (
        <PlanSection
          title="Material Plan"
          offsets={materialPlan.workflow_instance_offsets ?? []}
          milestones={resolvePlanMilestones(
            materialPlan.workflow_instance_milestones ?? [],
            MATERIAL_PLAN_MILESTONE_NAMES
          )}
          disabled={disabled}
          loading={isCalculatingMaterialPlan}
          onOffsetBlur={(offsetId, value) =>
            onOffsetBlur(offsetId, value, "material")
          }
        />
      ) : null}
    </div>
  );
}

function BidActivitiesGroup({
  linkedActivities,
  isCalculatingROJDate,
  requiredOnSiteValue,
  activitiesEditLinkAction,
  renderActivityCardTooltip,
  disabled,
  onLinkClick,
  activityWorkflowId,
  onsiteEstimateDateValue,
  scheduleDatePickerRef,
  onDateChange,
  footerForOnsiteEstimateDatePicker,
  onsiteEstimateLabel,
  onsiteEstimateLockedReason
}: Readonly<{
  linkedActivities: any[];
  isCalculatingROJDate: boolean;
  requiredOnSiteValue: React.ReactNode;
  activitiesEditLinkAction: React.ReactNode;
  renderActivityCardTooltip?: TRenderActivityCardTooltip;
  disabled?: boolean;
  onLinkClick: () => void;
  activityWorkflowId: string;
  onsiteEstimateDateValue: unknown;
  scheduleDatePickerRef: React.MutableRefObject<{ blur?: () => void } | null>;
  onDateChange: (workflowId: string, date: string | null) => void;
  footerForOnsiteEstimateDatePicker: React.ReactNode;
  onsiteEstimateLabel: string;
  /** Set when the Onsite Estimate cannot take a date; shown as the picker tooltip. */
  onsiteEstimateLockedReason?: string;
}>) {
  return (
    <div
      className="bid-date-planner-group"
      data-testid="links-column-group--11"
    >
      <div className="bid-date-planner-group__content bid-date-planner-group__content--padded">
        <Spin spinning={isCalculatingROJDate} size="small">
          <div
            className="bid-date-planner-required-row"
            data-testid="bid-required-on-site-row"
          >
            <span className="bid-date-planner-required-label">
              Required on site
            </span>
            {requiredOnSiteValue}
          </div>
        </Spin>

        <div
          className="bid-date-planner-linked-activity"
          data-testid="bid-linked-activity-container"
        >
          <div
            className="bid-date-planner-subheader"
            data-testid="bid-linked-activity-subheader"
          >
            <span className="bid-date-planner-group__label">
              Linked Activity
            </span>
            {activitiesEditLinkAction}
          </div>

          <div
            className="bid-date-planner-stack bid-date-planner-stack--scroll"
            data-testid="bid-activities-list"
          >
            {linkedActivities.map((link) => {
              const activityInstance = link.target_activities_instance;
              const card = (
                <LinkedEntityItem
                  title={activityInstance?.title}
                  testId="bid-linked-activity-item"
                />
              );
              return (
                <Fragment key={activityInstance?.id}>
                  {renderActivityCardTooltip
                    ? renderActivityCardTooltip(
                        card,
                        link,
                        mapPlanningActivityLinkToInstance(link)
                      )
                    : card}
                </Fragment>
              );
            })}
            {!disabled && linkedActivities.length === 0 ? (
              <div
                className="bid-date-planner-empty-row"
                data-testid="bid-activities-empty"
              >
                <span className="bid-date-planner-row-label">
                  No Activities Linked
                </span>
                <BidSuccessorsLinkAction
                  onClick={onLinkClick}
                  testId="bid-activities-link-button"
                >
                  Link
                </BidSuccessorsLinkAction>
              </div>
            ) : null}
          </div>
        </div>

        <div className="bid-date-planner-stack mt-2">
          <hr
            className="bid-date-planner-divider bid-date-planner-divider--stacked"
            data-testid="bid-onsite-estimate-divider"
          />

          <div
            className="bid-date-planner-inline-row"
            data-testid="bid-onsite-estimate-card"
          >
            <TruncatedTextWithTooltip
              text={onsiteEstimateLabel}
              className="bid-date-planner-inline-row__label bid-date-planner-row-label"
              dataTestId="bid-onsite-estimate-label"
            />
            <Tooltip title={onsiteEstimateLockedReason}>
              <div
                className="bid-date-planner-inline-row__control"
                data-testid="bid-onsite-estimate-control"
              >
                <Form.Item className="mb-0" name={activityWorkflowId}>
                  <CIQDatePicker
                    disabled={disabled || Boolean(onsiteEstimateLockedReason)}
                    ref={scheduleDatePickerRef}
                    className="w-full"
                    placeholder="Select date"
                    format={DATE_FORMAT_MMDDYYYY}
                    placement="topLeft"
                    // Keep the calendar icon on hover; clearing goes through the
                    // "Clear Date" footer, like Deadline Date.
                    allowClear={false}
                    onChange={(date: dayjs.Dayjs | null) => {
                      const saveDate = date
                        ? DateUtils.formatDateWithLunchTime(date)
                        : null;
                      onDateChange(activityWorkflowId, saveDate);
                    }}
                    renderExtraFooter={() =>
                      activityWorkflowId && onsiteEstimateDateValue
                        ? footerForOnsiteEstimateDatePicker
                        : null
                    }
                  />
                </Form.Item>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}

function requiredOnSiteDateNode(
  date: string | null | undefined,
  popover: ReactNode
) {
  if (!date) return null;
  return (
    <div className="flex items-center gap-1">
      <span className="bid-date-planner-required-value bid-date-planner-required-value--set">
        {DateUtils.format(date, DATE_FORMAT_MMDDYYYY)}
      </span>
      {popover ? (
        <Popover placement="left" content={popover} trigger="hover">
          <span
            className="flex items-center leading-none"
            data-testid="bid-required-on-site-info"
          >
            <BidDatePlannerInfoIcon />
          </span>
        </Popover>
      ) : null}
    </div>
  );
}

function BidPackageSuccessorsColumn(
  props: Readonly<{
    featureId?: string;
    entity?: TEntityDateblock;
    materialLinkCards?: React.ReactNode;
    submittalLinkCards?: React.ReactNode;
    afterChange?: () => void;
    disabled?: boolean;
    style?: React.CSSProperties;
    renderActivityCardTooltip?: TRenderActivityCardTooltip;
  }>
) {
  const {
    featureId,
    entity,
    materialLinkCards,
    submittalLinkCards,
    afterChange,
    disabled,
    style,
    renderActivityCardTooltip
  } = props;
  const [form] = Form.useForm();
  const { tokenRetrievalState, columnHeaders } = useContext(ProjectContext);
  const token = tokenRetrievalState?.token ?? "";
  // Project column header config (Settings > Rename Fields), same label as the log column.
  const onsiteEstimateLabel =
    columnHeaders?.bidPackageHeaderMap?.onsite_estimate_date ||
    "Onsite Estimate";

  const [linkMode, setLinkMode] = useState<TLinkMode>("activity");
  const [isActivityLinkingDialogOpen, setIsActivityLinkingDialogOpen] =
    useState(false);
  const [isMaterialLinkingDialogOpen, setIsMaterialLinkingDialogOpen] =
    useState(false);
  const [isSubmittalLinkingDialogOpen, setIsSubmittalLinkingDialogOpen] =
    useState(false);
  const [isMaterialEditMode, setIsMaterialEditMode] = useState(false);
  const [isSubmittalEditMode, setIsSubmittalEditMode] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState<TLinkedMaterial[]>(
    []
  );
  const [selectedSubmittals, setSelectedSubmittals] = useState<
    TLinkedSubmittal[]
  >([]);
  const [isCalculatingSubmittalPlan, setIsCalculatingSubmittalPlan] =
    useState(false);
  const [isCalculatingMaterialPlan, setIsCalculatingMaterialPlan] =
    useState(false);
  const [isCalculatingROJDate, setIsCalculatingROJDate] = useState(false);

  const scheduleDatePickerRef = useRef<{ blur?: () => void } | null>(null);
  const prevIncomingFieldValuesRef = useRef<Record<string, unknown> | null>(
    null
  );

  const {
    featureLinks,
    updateFeatureWorkflowInstance,
    refetch: refetchPlanningData
  } = useBidPackagePlanning({
    featureId: featureId ?? "",
    token,
    onRefetchCallback: () => afterChange?.()
  });

  // Same source as the Bid Package Log, so both pages lock the Onsite Estimate
  // for the same bid packages. Re-read whenever the planning data reloads
  // (after every link or date change), since linking can change the flag.
  const [onsiteEstimateEditable, setOnsiteEstimateEditable] = useState<
    boolean | null
  >(null);
  useEffect(() => {
    setOnsiteEstimateEditable(null);
  }, [featureId]);
  useEffect(() => {
    if (!featureId || !token || !featureLinks) return undefined;
    let cancelled = false;
    getGQLQueryData(token, QUERY_BID_PACKAGE_ONSITE_ESTIMATE_EDITABLE, {
      id: featureId
    }).then((response) => {
      if (cancelled || !response.success) return;
      const row: Pick<TBidPackageLog, "onsite_estimate_editable"> | undefined =
        response.data?.data?.bid_package_list_func?.[0];
      setOnsiteEstimateEditable(row?.onsite_estimate_editable ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [featureId, token, featureLinks]);

  const linkedMaterials = useMemo(
    () => getBidPackageLinkedMaterials(entity),
    [entity]
  );

  const linkedSubmittals = useMemo(
    () => getBidPackageLinkedSubmittals(entity),
    [entity]
  );

  const {
    linkedFeatures: linkedMaterialFeatures,
    featuresList: materialFeaturesList,
    saveMaterialBidPackageLinks,
    isLoadingLinkedFeatures: isLoadingLinkedMaterialFeatures,
    isLoadingFeaturesList: isLoadingMaterialFeaturesList,
    isSavingLinks: isSavingMaterialLinks,
    linkedFeaturesError: linkedMaterialFeaturesError,
    resetFeaturesListStates: resetMaterialFeaturesListStates
  } = useFeatureToFeatureLinks({
    token,
    featureInstanceId: featureId ?? "",
    featureTypeId: FeatureTypes.MATERIALS,
    linkType: "child",
    loadLinkedFeatures: Boolean(featureId),
    loadFeaturesList: isMaterialLinkingDialogOpen,
    featureListWhere: MATERIAL_FEATURES_LIST_WHERE
  });

  const {
    linkedFeatures: linkedSubmittalFeatures,
    featuresList: submittalFeaturesList,
    saveLinks: saveSubmittalLinks,
    isLoadingLinkedFeatures: isLoadingLinkedSubmittalFeatures,
    isLoadingFeaturesList: isLoadingSubmittalFeaturesList,
    isSavingLinks: isSavingSubmittalLinks,
    linkedFeaturesError: linkedSubmittalFeaturesError,
    resetFeaturesListStates: resetSubmittalFeaturesListStates
  } = useFeatureToFeatureLinks({
    token,
    featureInstanceId: featureId ?? "",
    featureTypeId: FeatureTypes.SUBMITTALS,
    linkType: "child",
    loadLinkedFeatures: Boolean(featureId),
    loadFeaturesList: isSubmittalLinkingDialogOpen,
    featureListWhere: SUBMITTAL_FEATURES_LIST_WHERE
  });

  const linkedMaterialsForDialog = useMemo(
    () => mapLinkedMaterialsForDialog(linkedMaterialFeatures, linkedMaterials),
    [linkedMaterialFeatures, linkedMaterials]
  );

  const linkedSubmittalsForDialog = useMemo(
    () =>
      mapLinkedSubmittalsForDialog(linkedSubmittalFeatures, linkedSubmittals),
    [linkedSubmittalFeatures, linkedSubmittals]
  );

  const isMaterialLinkingInteractionDisabled =
    isSavingMaterialLinks ||
    isLoadingLinkedMaterialFeatures ||
    isLoadingMaterialFeaturesList;

  const isSubmittalLinkingInteractionDisabled =
    isSavingSubmittalLinks ||
    isLoadingLinkedSubmittalFeatures ||
    isLoadingSubmittalFeaturesList;

  const handleLoadingIndicators = useCallback(
    (feature: "submittal" | "material" | "schedule") => {
      // `feature` is an exhaustive three-value union, so this guard is always
      // true and its implicit else can never run.
      /* v8 ignore next */
      if (
        feature === "submittal" ||
        feature === "material" ||
        feature === "schedule"
      ) {
        setIsCalculatingSubmittalPlan(true);
        setTimeout(() => setIsCalculatingSubmittalPlan(false), 4000);
      }
      if (feature === "material" || feature === "schedule") {
        setIsCalculatingMaterialPlan(true);
        setTimeout(() => setIsCalculatingMaterialPlan(false), 4000);
      }
      if (feature === "schedule") {
        setIsCalculatingROJDate(true);
        setTimeout(() => setIsCalculatingROJDate(false), 4000);
      }
    },
    []
  );

  const linkedActivities = useMemo(() => {
    const activities =
      featureLinks?.PLANNING_ONLY_MATERIAL?.links_as_source?.filter(
        (link) => link.target_activities_instance?.feature_activities?.length
      ) ?? [];

    activities.sort((a, b) => {
      const aActivity = a.target_activities_instance?.feature_activities[0];
      const bActivity = b.target_activities_instance?.feature_activities[0];
      const aDateString =
        a.link_type === (LinkTypes.finish_to_start as number)
          ? aActivity?.start_date
          : aActivity?.end_date;
      const bDateString =
        b.link_type === (LinkTypes.finish_to_start as number)
          ? bActivity?.start_date
          : bActivity?.end_date;
      return dayjs(aDateString).diff(dayjs(bDateString));
    });

    return activities;
  }, [featureLinks]);

  const incomingFieldValues = useMemo(() => {
    if (!featureLinks) return null;

    const fieldValuesToSet: Record<string, unknown> = {};

    Object.keys(featureLinks).forEach((key) => {
      featureLinks?.[
        key as keyof typeof featureLinks
      ]?.workflow_instance_offsets?.forEach(
        (offset: WorkflowInstanceOffset) => {
          fieldValuesToSet[offset.id] = offset.duration;
        }
      );
    });

    const activityWfId =
      featureLinks?.PLANNING_ONLY_ACTIVITY?.feature_workflow_instance?.id ?? "";
    const manualDate =
      featureLinks?.PLANNING_ONLY_ACTIVITY?.feature_workflow_instance
        ?.manual_entry_date;
    fieldValuesToSet[activityWfId] = manualDate
      ? DateUtils.dateTimeObj(manualDate)
      : null;

    return fieldValuesToSet;
  }, [featureLinks]);

  const governingEntity = useMemo(() => {
    if (featureLinks?.PLANNING_ONLY_ACTIVITY?.governing_link) {
      return {
        governedBy: "manual_date" as const,
        date: featureLinks?.PLANNING_ONLY_ACTIVITY?.feature_workflow_instance
          ?.manual_entry_date
      };
    }

    const activities =
      featureLinks?.PLANNING_ONLY_MATERIAL?.links_as_source?.filter(
        (link) => link.target_activities_instance?.feature_activities?.length
      );
    const governingActivity = activities?.filter(
      (activity) => activity.governing_link
    );

    if (governingActivity && governingActivity.length > 0) {
      const linkDate =
        governingActivity[0]?.link_type ===
        (LinkTypes.finish_to_start as number)
          ? governingActivity[0]?.target_activities_instance
              ?.feature_activities[0]?.start_date
          : governingActivity[0]?.target_activities_instance
              ?.feature_activities[0]?.end_date;
      return {
        governedBy: "linked_activity" as const,
        date: linkDate,
        governingActivity: governingActivity[0]
      };
    }

    return null;
  }, [featureLinks]);

  const onOffsetBlur = async (
    offsetId: string,
    value: string | null,
    feature: "submittal" | "material"
  ) => {
    if (incomingFieldValues?.[offsetId] === value) return;

    const parsed = value ? Number.parseInt(value, 10) : null;
    const input = [
      {
        feature_instance_id:
          feature === "submittal"
            ? featureLinks?.PLANNING_ONLY_SUBMITTAL?.id
            : featureLinks?.PLANNING_ONLY_MATERIAL?.id,
        workflow_instance_id:
          featureLinks?.PLANNING_ONLY_ACTIVITY?.feature_workflow_instance?.id,
        offsets: [{ id: offsetId, duration: parsed }]
      }
    ];

    const updateResponse = await updateFeatureWorkflowInstance({ input });
    if (updateResponse.success) {
      handleLoadingIndicators(feature);
    }
  };

  const onDateChange = async (
    workflowInstanceId: string,
    value: string | null
  ) => {
    if (!featureId) return;

    // The form sync effect only writes changed incoming values, so an unsaved
    // pick over an empty date would otherwise stay on screen.
    const revertPickedDate = () =>
      form.setFieldsValue({
        [workflowInstanceId]: incomingFieldValues?.[workflowInstanceId] ?? null
      });

    const updateResponse = await linkBidPackages(
      {
        bid_package_ids: [featureId],
        planning_only_activity_date: value,
        skip_completed: true
      },
      token
    );
    if (!updateResponse.success) {
      message.error(updateResponse.errorMsg || "Failed to update.");
      revertPickedDate();
      return;
    }

    const summary = updateResponse.data as
      | Partial<TOnsiteEstimateUpdateSummary>
      | undefined;
    if (summary?.updated_ids?.length === 0) {
      message.error("This Bid Package cannot be updated.");
      revertPickedDate();
      refetchPlanningData();
      afterChange?.();
      return;
    }

    handleLoadingIndicators("schedule");
    refetchPlanningData();
    afterChange?.();
  };

  useEffect(() => {
    if (!featureLinks || !incomingFieldValues) return;

    const changedValues: Record<string, unknown> = {};
    Object.keys(incomingFieldValues).forEach((key) => {
      if (
        incomingFieldValues[key] !== prevIncomingFieldValuesRef.current?.[key]
      ) {
        changedValues[key] = incomingFieldValues[key];
      }
    });

    form.setFieldsValue(changedValues);
    prevIncomingFieldValuesRef.current = incomingFieldValues;
  }, [featureLinks, form, incomingFieldValues]);

  const openMaterialLinkingDialog = () => {
    setSelectedMaterials(linkedMaterialsForDialog);
    setIsMaterialEditMode(false);
    setIsMaterialLinkingDialogOpen(true);
  };

  const openSubmittalLinkingDialog = () => {
    setSelectedSubmittals(linkedSubmittalsForDialog);
    setIsSubmittalEditMode(false);
    setIsSubmittalLinkingDialogOpen(true);
  };

  const onMaterialCheckBoxAction = (data: {
    checked: boolean;
    feature: TLinkedMaterial;
  }) => {
    setIsMaterialEditMode(true);
    setSelectedMaterials((prev) => {
      if (data.checked) {
        return [data.feature, ...prev];
      }

      return prev.filter((feature) => feature.id !== data.feature.id);
    });
  };

  const onSubmittalCheckBoxAction = (data: {
    checked: boolean;
    feature: TLinkedSubmittal;
  }) => {
    setIsSubmittalEditMode(true);
    setSelectedSubmittals((prev) => {
      if (data.checked) {
        return [data.feature, ...prev];
      }

      return prev.filter((feature) => feature.id !== data.feature.id);
    });
  };

  const onMaterialUnlink = useCallback((feature: TLinkedMaterial) => {
    setIsMaterialEditMode(true);
    setSelectedMaterials((prev) => prev.filter((f) => f.id !== feature.id));
  }, []);

  const onSubmittalUnlink = useCallback((feature: TLinkedSubmittal) => {
    setIsSubmittalEditMode(true);
    setSelectedSubmittals((prev) => prev.filter((f) => f.id !== feature.id));
  }, []);

  const onMaterialDialogCancel = () => {
    setIsMaterialLinkingDialogOpen(false);
    resetMaterialFeaturesListStates();
    setSelectedMaterials(linkedMaterialsForDialog);
    setIsMaterialEditMode(false);
  };

  const onSubmittalDialogCancel = () => {
    setIsSubmittalLinkingDialogOpen(false);
    resetSubmittalFeaturesListStates();
    setSelectedSubmittals(linkedSubmittalsForDialog);
    setIsSubmittalEditMode(false);
  };

  const onMaterialSave = async () => {
    if (!featureId) return;

    const changes = buildMaterialLinkChanges(
      selectedMaterials,
      linkedMaterialsForDialog
    );
    if (!changes.insertedLinks.length && !changes.deletedLinks.length) return;

    const response = await saveMaterialBidPackageLinks({
      bid_package_ids: [featureId],
      ...(changes.insertedLinks.length && {
        link_material_ids: changes.insertedLinks.map((material) => material.id)
      }),
      ...(changes.deletedLinks.length && {
        unlink_material_ids: changes.deletedLinks.map((material) => material.id)
      })
    });

    if (response.success) {
      setIsMaterialLinkingDialogOpen(false);
      resetMaterialFeaturesListStates();
      setIsMaterialEditMode(false);
      message.success("Material links updated successfully");
      handleLoadingIndicators("material");
      refetchPlanningData();
      afterChange?.();
    }
  };

  const onSubmittalSave = async () => {
    if (!featureId) return;

    const changes = buildSubmittalLinkChanges(
      selectedSubmittals,
      linkedSubmittalsForDialog
    );
    if (!changes.insertedLinks.length && !changes.deletedLinks.length) return;

    const response = await saveSubmittalLinks({
      input: {
        deletes: changes.deletedLinks.map((submittal) => ({
          source_instance_id: featureId,
          target_instance_id: submittal.id
        })),
        inserts: changes.insertedLinks.map((submittal) => ({
          source_instance_id: featureId,
          target_instance_id: submittal.id,
          link_type: LinkTypes.finish_to_start,
          coupling_type: 2
        })),
        updates: []
      },
      metadata: {
        request_source: "DETAIL_PAGE"
      }
    });

    if (response.success) {
      setIsSubmittalLinkingDialogOpen(false);
      resetSubmittalFeaturesListStates();
      setIsSubmittalEditMode(false);
      message.success("Submittal links updated successfully");
      handleLoadingIndicators("submittal");
      refetchPlanningData();
      afterChange?.();
    }
  };

  const materialIdsWithSubmittals = useMemo(
    () => getFeatureIdsWithLinks(materialFeaturesList, "linked_submittals"),
    [materialFeaturesList]
  );
  const submittalIdsWithMaterials = useMemo(
    () => getFeatureIdsWithLinks(submittalFeaturesList, "linked_materials"),
    [submittalFeaturesList]
  );
  const anySelectedMaterialHasSubmittals = selectedMaterials.some((material) =>
    materialIdsWithSubmittals.has(material.id)
  );
  const anySelectedSubmittalHasMaterials = selectedSubmittals.some(
    (submittal) => submittalIdsWithMaterials.has(submittal.id)
  );

  const linkedMaterialsDOMForDialog = useMemo(() => {
    // `selectedMaterials` is state seeded with `[]` and only ever set to
    // arrays, so `!selectedMaterials` is never true and the loading operand is
    // dead.
    /* v8 ignore next */
    if (
      (!selectedMaterials && isLoadingLinkedMaterialFeatures) ||
      linkedMaterialFeaturesError
    ) {
      return null;
    }

    if (selectedMaterials.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-xs italic text-ciq-gray-950 opacity-80 pt-14">
          No materials linked
        </div>
      );
    }

    const sortedMaterials = sortLinkedItems(
      selectedMaterials,
      "auto_inc_value"
    );

    return (
      <div className="space-y-2">
        {sortedMaterials.map((material) => (
          <FeatureItem
            key={material.id}
            variant="compact"
            number={String(material.auto_inc_value ?? "")}
            title={material.title ?? ""}
            disableUnlink={isMaterialLinkingInteractionDisabled}
            onUnlink={() => onMaterialUnlink(material)}
            nestedInfoTooltip={
              materialIdsWithSubmittals.has(material.id)
                ? "Material has linked submittal(s)."
                : undefined
            }
          />
        ))}
      </div>
    );
  }, [
    materialIdsWithSubmittals,
    isLoadingLinkedMaterialFeatures,
    isMaterialLinkingInteractionDisabled,
    linkedMaterialFeaturesError,
    onMaterialUnlink,
    selectedMaterials
  ]);

  const linkedSubmittalsDOMForDialog = useMemo(() => {
    /* v8 ignore next */
    if (
      (!selectedSubmittals && isLoadingLinkedSubmittalFeatures) ||
      linkedSubmittalFeaturesError
    ) {
      return null;
    }

    if (selectedSubmittals.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-xs italic text-ciq-gray-950 opacity-80 pt-14">
          No submittals linked
        </div>
      );
    }

    const sortedSubmittals = sortLinkedItems(
      selectedSubmittals,
      "auto_inc_value"
    );

    return (
      <div className="space-y-2">
        {sortedSubmittals.map((submittal) => (
          <FeatureItem
            key={submittal.id}
            variant="compact"
            number={String(submittal.auto_inc_value ?? "")}
            title={submittal.title ?? ""}
            disableUnlink={isSubmittalLinkingInteractionDisabled}
            onUnlink={() => onSubmittalUnlink(submittal)}
            nestedInfoTooltip={
              submittalIdsWithMaterials.has(submittal.id)
                ? "Submittal has linked material(s)."
                : undefined
            }
          />
        ))}
      </div>
    );
  }, [
    submittalIdsWithMaterials,
    isLoadingLinkedSubmittalFeatures,
    isSubmittalLinkingInteractionDisabled,
    linkedSubmittalFeaturesError,
    onSubmittalUnlink,
    selectedSubmittals
  ]);

  const activityWorkflowId =
    featureLinks?.PLANNING_ONLY_ACTIVITY?.feature_workflow_instance?.id ?? "";

  const onsiteEstimateDateValue = incomingFieldValues?.[activityWorkflowId];

  // Same rules as the Bid Package Log's Onsite Estimate column and bulk edit.
  let onsiteEstimateLockedReason: string | undefined;
  if (isBidPackageCompleted(entity?.feature_workflow_instance)) {
    onsiteEstimateLockedReason = `${onsiteEstimateLabel} cannot be changed for a completed Bid Package.`;
  } else if (
    !isOnsiteEstimateEditable({
      onsite_estimate_editable: onsiteEstimateEditable
    })
  ) {
    onsiteEstimateLockedReason =
      "Required on site is planned through linked Materials or Submittals.";
  }

  const footerForOnsiteEstimateDatePicker = (
    <div className="flex justify-center py-2">
      <Button
        disabled={disabled}
        onClick={() => {
          onDateChange(activityWorkflowId, null);
          form.setFieldsValue({ [activityWorkflowId]: null });
          setTimeout(() => {
            scheduleDatePickerRef.current?.blur?.();
          });
        }}
        data-testid="bid-onsite-estimate-clear-date"
      >
        Clear Date
      </Button>
    </div>
  );

  const submittalPlan = featureLinks?.PLANNING_ONLY_SUBMITTAL;
  const materialPlan = featureLinks?.PLANNING_ONLY_MATERIAL;

  const openActivityLinkingDialog = () => setIsActivityLinkingDialogOpen(true);

  const materialHeaderEditLinks =
    !disabled && linkedMaterials.length > 0 ? (
      <BidSuccessorsLinkAction
        onClick={openMaterialLinkingDialog}
        testId="bid-materials-edit-links"
      >
        Edit Link
      </BidSuccessorsLinkAction>
    ) : null;

  const submittalHeaderEditLinks =
    !disabled && linkedSubmittals.length > 0 ? (
      <BidSuccessorsLinkAction
        onClick={openSubmittalLinkingDialog}
        testId="bid-submittals-edit-links"
      >
        Edit Link
      </BidSuccessorsLinkAction>
    ) : null;

  const requiredOnSitePopoverContent = useMemo(() => {
    if (!governingEntity) return null;

    if (governingEntity.governedBy === "manual_date") {
      return (
        <div className="text-xs text-ciq-gray-950 opacity-80 max-w-[200px]">
          Governing Entity: Manually Set Date.
        </div>
      );
    }

    const activityInstance =
      governingEntity.governingActivity?.target_activities_instance;
    const sourceTaskId =
      activityInstance?.feature_activities?.[0]?.source_task_id;
    const title = activityInstance?.title;

    return (
      <div className="text-xs text-ciq-gray-950 opacity-80 max-w-[200px] space-y-0.5">
        <div>Governing Entity:</div>
        <div>
          {sourceTaskId} - {title}
        </div>
      </div>
    );
  }, [governingEntity]);

  const activitiesHeaderEditLinks =
    !disabled && linkedActivities.length > 0 ? (
      <BidSuccessorsLinkAction
        onClick={openActivityLinkingDialog}
        testId="bid-activities-edit-links"
      >
        Edit Link
      </BidSuccessorsLinkAction>
    ) : null;

  const requiredOnSiteDate = requiredOnSiteDateNode(
    governingEntity?.date,
    requiredOnSitePopoverContent
  );

  // Linking actions live on the "Linked Activity" sub-header / empty row, so
  // the Required on site row only shows the date and its info icon.
  const bidPackageName = formatEntityTitle(
    (entity as { number?: string | number | null } | undefined)?.number ??
      entity?.auto_inc_value,
    entity?.title
  );

  const requiredOnSiteValue = requiredOnSiteDate ?? (
    <span className="bid-date-planner-required-value">-</span>
  );

  return (
    <div
      className="shrink-0 flex flex-col self-stretch -mb-9"
      style={{ width: BID_PACKAGE_SUCCESSORS_COLUMN_WIDTH, ...style }}
      data-testid="entity-dateblock-new-interface-successors"
    >
      <div
        className="min-w-0 flex flex-1 flex-col"
        style={{
          paddingRight: LINKS_COLUMN_OUTER_PADDING_PX
        }}
      >
        <div
          className="bid-date-planner-panel"
          data-testid="bid-date-planner-panel"
        >
          <div className="bid-date-planner-panel__title">Date planner</div>

          <BidDatePlannerTabs
            value={linkMode}
            onChange={setLinkMode}
            activityCount={linkedActivities.length}
            materialCount={linkedMaterials.length}
            submittalCount={linkedSubmittals.length}
          />
          <p className="bid-date-planner-panel__description">
            {DATE_PLANNER_DESCRIPTION_BY_MODE[linkMode]}
          </p>

          {linkMode === "activity" ? (
            <Form form={form} layout="vertical">
              <BidActivitiesGroup
                linkedActivities={linkedActivities}
                isCalculatingROJDate={isCalculatingROJDate}
                requiredOnSiteValue={requiredOnSiteValue}
                activitiesEditLinkAction={activitiesHeaderEditLinks}
                renderActivityCardTooltip={renderActivityCardTooltip}
                disabled={disabled}
                onLinkClick={openActivityLinkingDialog}
                activityWorkflowId={activityWorkflowId}
                onsiteEstimateDateValue={onsiteEstimateDateValue}
                scheduleDatePickerRef={scheduleDatePickerRef}
                onDateChange={onDateChange}
                footerForOnsiteEstimateDatePicker={
                  footerForOnsiteEstimateDatePicker
                }
                onsiteEstimateLabel={onsiteEstimateLabel}
                onsiteEstimateLockedReason={onsiteEstimateLockedReason}
              />

              <hr className="bid-date-planner-divider bid-date-planner-divider--dotted" />

              <BidPlanSections
                submittalPlan={submittalPlan}
                materialPlan={materialPlan}
                disabled={disabled}
                isCalculatingSubmittalPlan={isCalculatingSubmittalPlan}
                isCalculatingMaterialPlan={isCalculatingMaterialPlan}
                onOffsetBlur={onOffsetBlur}
              />
            </Form>
          ) : null}

          {linkMode === "material" ? (
            <MaterialLinkGroup
              linkedMaterials={linkedMaterials}
              materialLinkCards={materialLinkCards}
              materialHeaderEditLinks={materialHeaderEditLinks}
              disabled={disabled}
              onLinkClick={openMaterialLinkingDialog}
            />
          ) : null}

          {linkMode === "submittal" ? (
            <SubmittalLinkGroup
              linkedSubmittals={linkedSubmittals}
              submittalLinkCards={submittalLinkCards}
              submittalHeaderEditLinks={submittalHeaderEditLinks}
              disabled={disabled}
              onLinkClick={openSubmittalLinkingDialog}
            />
          ) : null}
        </div>
      </div>

      {isActivityLinkingDialogOpen ? (
        <FeatureActivityLinking
          dialogOpenState={[
            isActivityLinkingDialogOpen,
            setIsActivityLinkingDialogOpen
          ]}
          featureId={featureId}
          linkedFeatureId={featureLinks?.PLANNING_ONLY_MATERIAL?.id}
          bidPackageName={bidPackageName}
          onSaveSuccess={() => {
            handleLoadingIndicators("schedule");
            refetchPlanningData();
          }}
        />
      ) : null}
      <FeatureLinkingDialog
        open={isMaterialLinkingDialogOpen}
        variant="manage"
        title={
          <ManageLinksDialogTitle
            title="Manage linked Materials"
            bidPackageName={bidPackageName}
          />
        }
        onClose={onMaterialDialogCancel}
        footerComponent={
          <FeatureLinksDialogHeader
            placement="footer"
            onCancel={onMaterialDialogCancel}
            onSave={onMaterialSave}
            disableSaveButton={!isMaterialEditMode}
            disableCancelButton={isSavingMaterialLinks}
            saveButtonLoading={isSavingMaterialLinks}
          />
        }
        lhsComponent={
          <div className="h-full max-h-full min-w-0 overflow-hidden flex flex-col">
            {anySelectedMaterialHasSubmittals ? (
              <div className="mb-2 px-4 text-xs text-ciq-gray-800">
                Linked material will have linked submittals
              </div>
            ) : null}
            <div className="min-w-0 grow overflow-x-hidden overflow-y-auto px-4">
              {linkedMaterialsDOMForDialog}
            </div>
          </div>
        }
        rhsComponent={
          <FeaturesGrid
            variant="manage"
            searchPlaceholder="Search Materials"
            checkboxColumnField="auto_inc_value"
            featuresList={materialFeaturesList}
            onCheckBoxAction={onMaterialCheckBoxAction}
            linkedFeatures={selectedMaterials}
            isLoadingFeaturesList={isLoadingMaterialFeaturesList}
            disableCheckbox={isMaterialLinkingInteractionDisabled}
            headerLabel="Link Materials"
            emptyLabel="No Materials found"
          />
        }
      />
      <FeatureLinkingDialog
        open={isSubmittalLinkingDialogOpen}
        variant="manage"
        title={
          <ManageLinksDialogTitle
            title="Manage linked Submittals"
            bidPackageName={bidPackageName}
          />
        }
        onClose={onSubmittalDialogCancel}
        footerComponent={
          <FeatureLinksDialogHeader
            placement="footer"
            onCancel={onSubmittalDialogCancel}
            onSave={onSubmittalSave}
            disableSaveButton={!isSubmittalEditMode}
            disableCancelButton={isSavingSubmittalLinks}
            saveButtonLoading={isSavingSubmittalLinks}
          />
        }
        lhsComponent={
          <div className="h-full max-h-full min-w-0 overflow-hidden flex flex-col">
            {anySelectedSubmittalHasMaterials ? (
              <div className="mb-2 px-4 text-xs text-ciq-gray-800">
                Linked submittal will have linked materials
              </div>
            ) : null}
            <div className="min-w-0 grow overflow-x-hidden overflow-y-auto px-4">
              {linkedSubmittalsDOMForDialog}
            </div>
          </div>
        }
        rhsComponent={
          <FeaturesGrid
            variant="manage"
            searchPlaceholder="Search Submittals"
            checkboxColumnField="auto_inc_value"
            featuresList={submittalFeaturesList}
            onCheckBoxAction={onSubmittalCheckBoxAction}
            linkedFeatures={selectedSubmittals}
            isLoadingFeaturesList={isLoadingSubmittalFeaturesList}
            disableCheckbox={isSubmittalLinkingInteractionDisabled}
            headerLabel="Link Submittals"
            emptyLabel="No Submittals found"
          />
        }
      />
    </div>
  );
}

export default BidPackageSuccessorsColumn;
